import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { 
  Card, 
  Row, 
  Col, 
  Tag,
  Space, 
  Button, 
  Typography, 
  Descriptions, 
  Tabs, 
  Table,
  Alert,
  Timeline,
  Avatar,
  Divider,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Badge,
  Tooltip,
  List,
  Calendar,
  Comment,
  DatePicker,
  Popconfirm
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  HomeOutlined,
  TeamOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  DollarOutlined,
  FileTextOutlined,
  GlobalOutlined,
  MessageOutlined,
  HistoryOutlined,
  ContactsOutlined,
  PlusOutlined,
  StopOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { safeFormatTimestamp } from '../../utils/timezone';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import websocketService from '../../services/websocket';
import { handleApiError } from '../../utils/errorHandler';
import {
  customerNameRules,
  contactPersonRules,
  emailRules,
  phoneRules,
  addressRules,
  taxNumberRules,
  usernameRules,
  fullNameRules,
  passwordRules,
  passwordHelpText,
} from '../../utils/validationRules';
import logger from '../../services/logger';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const CustomerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [usageAnalytics, setUsageAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [userModalTab, setUserModalTab] = useState('existing');
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [communicationModalVisible, setCommunicationModalVisible] = useState(false);
  const [passwordPolicy, setPasswordPolicy] = useState(null);
  const [form] = Form.useForm();
  const [communicationForm] = Form.useForm();
  const [userForm] = Form.useForm();
  
  // URL'den tab bilgisini al
  const searchParams = new URLSearchParams(window.location.search);
  const activeTab = searchParams.get('tab') || 'overview';

  const fetchUsageAnalytics = useCallback(async (startDate, endDate) => {
    try {
      setAnalyticsLoading(true);
      const params = new URLSearchParams();
      if (startDate) {
        params.append('start_date', startDate.toISOString());
      }
      if (endDate) {
        params.append('end_date', endDate.toISOString());
      }
      
      const analyticsResponse = await axios.get(`/api/v1/customers/${id}/usage-analytics?${params.toString()}`);
      logger.info('Kullanım analizi yanıtı:', analyticsResponse.data);
      setUsageAnalytics(analyticsResponse.data);
    } catch (error) {
      logger.error('Kullanım analizi yüklenirken hata:', error);
      message.error('Kullanım analizi yüklenirken hata oluştu');
      setUsageAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [id]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
      fetchUsageAnalytics(dates[0].toDate(), dates[1].toDate());
    }
  };

  const fetchCustomerUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const response = await axios.get(`/api/v1/customers/${id}/users`);
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Customer users fetch error:', error);
      message.error('Kullanıcılar yüklenirken hata oluştu');
    } finally {
      setUsersLoading(false);
    }
  }, [id]);

  const fetchAvailableUsers = useCallback(async () => {
    try {
      const response = await axios.get('/api/v1/users');
      const allUsers = Array.isArray(response.data) ? response.data : [];
      // Sadece customer rolündeki kullanıcıları filtrele
      const customerUsers = allUsers.filter(u => u.role?.name === 'customer');
      setAvailableUsers(customerUsers);
    } catch (error) {
      logger.error('Available users fetch error:', error);
    }
  }, []);

  const handleAddExistingUser = async (values) => {
    try {
      await axios.post(`/api/v1/customers/${id}/users`, {
        user_id: values.user_id
      });
      message.success('Kullanıcı başarıyla eklendi');
      fetchCustomerUsers();
      setUserModalVisible(false);
      userForm.resetFields();
    } catch (error) {
      handleApiError(error, 'Kullanıcı eklenirken hata oluştu', message.error);
    }
  };

  const handleCreateNewUser = async (values) => {
    try {
      await axios.post(`/api/v1/customers/${id}/users/new`, values);
      message.success('Kullanıcı başarıyla oluşturuldu ve eklendi');
      fetchCustomerUsers();
      setUserModalVisible(false);
      userForm.resetFields();
    } catch (error) {
      handleApiError(error, 'Kullanıcı oluşturulurken hata oluştu', message.error);
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await axios.delete(`/api/v1/customers/${id}/users/${userId}`);
      message.success('Kullanıcı başarıyla kaldırıldı');
      fetchCustomerUsers();
    } catch (error) {
      handleApiError(error, 'Kullanıcı kaldırılırken hata oluştu', message.error);
    }
  };

  const fetchCustomerData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch customer details
      const customerResponse = await axios.get(`/api/v1/customers/${id}`);
      setCustomer(customerResponse.data);

      // Fetch customer devices
      try {
        const devicesResponse = await axios.get(`/api/v1/devices?customer_id=${id}`);
        setDevices(devicesResponse.data);
      } catch (error) {
        logger.error('Cihazlar yüklenirken hata:', error);
        setDevices([]);
      }

      // Fetch customer invoices
      try {
        const invoicesResponse = await axios.get(`/api/v1/billing/invoices?customer_id=${id}`);
        logger.info('Fatura yanıtı:', invoicesResponse.data);
        setInvoices(invoicesResponse.data);
      } catch (error) {
        logger.error('Faturalar yüklenirken hata:', error);
        setInvoices([]);
      }

      // Fetch customer rentals
      try {
        const rentalsResponse = await axios.get(`/api/v1/rentals?customer_id=${id}`);
        setRentals(rentalsResponse.data);
      } catch (error) {
        logger.error('Kiralamalar yüklenirken hata:', error);
        setRentals([]);
      }

      // Usage analytics'i ilk yüklemede çağırma, tarih seçildikten sonra çağır
      // setUsageAnalytics(null); // İlk yüklemede boş bırak

      // Communications feature is not implemented yet
      setCommunications([]);

    } catch (error) {
      logger.error('Müşteri bilgileri yüklenirken hata:', error);
      handleApiError(error, 'Müşteri bilgileri yüklenirken hata oluştu', message.error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCustomerData();
    fetchCustomerUsers();
    fetchAvailableUsers();
    // Password policy fetch (yeni kullanıcı oluşturma formu için)
    (async () => {
      try {
        const response = await axios.get('/api/v1/profile/password-requirements');
        setPasswordPolicy(response.data);
      } catch (error) {
        logger.error('Password policy fetch error:', error);
      }
    })();
    // İlk yüklemede son 30 günün verisini çek
    fetchUsageAnalytics(dateRange[0].toDate(), dateRange[1].toDate());

    // WebSocket ile cihaz durumu güncellemelerini dinle
    const token = localStorage.getItem('token');
    logger.debug('🔍 CustomerProfile.js - Token kontrolü:', token ? 'Token var' : 'Token yok');
    logger.debug('🔍 CustomerProfile.js - WebSocketService:', websocketService ? 'Mevcut' : 'Yok');

    if (token && websocketService) {
      try {
        logger.debug('🔍 CustomerProfile.js - WebSocket bağlantısı başlatılıyor...');
        websocketService.connect(token);

        // Cihaz durumu güncellemelerini dinle
        const unsubscribeDeviceStatus = websocketService.subscribe('device_status', (data) => {
          logger.debug('🔍 CustomerProfile.js - Device status güncellemesi alındı:', data);

          if (data.type === 'device_status') {
            logger.debug(`🔍 CustomerProfile.js - Cihaz ${data.device_id} durumu güncelleniyor: ${data.status}`);

            // Cihaz listesini güncelle (sadece bu müşteriye ait cihazlar)
            setDevices(prevDevices => {
              const updatedDevices = prevDevices.map(device => {
                if (device.id === data.device_id) {
                  logger.info(`✅ CustomerProfile.js - Cihaz ${device.name} durumu güncellendi: ${device.status} → ${data.status}`);
                  return {
                    ...device,
                    status: data.status,
                    last_seen: data.data?.timestamp || new Date().toISOString()
                  };
                }
                return device;
              });

              return updatedDevices;
            });
          }
        });

        logger.info('✅ CustomerProfile.js - WebSocket device_status listener eklendi');

        return () => {
          logger.debug('🔍 CustomerProfile.js - Cleanup çalıştırılıyor');
          unsubscribeDeviceStatus();
        };
      } catch (error) {
        logger.error('❌ CustomerProfile.js - WebSocket bağlantı hatası:', error);
      }
    } else {
      logger.warn('⚠️ CustomerProfile.js - WebSocket bağlantısı kurulamadı (token veya service yok)');
    }
  }, [fetchCustomerData, fetchCustomerUsers, fetchAvailableUsers, fetchUsageAnalytics, dateRange]);

  const getStatusTag = (status) => {
    const statusConfig = {
      active: { color: 'green', icon: <CheckCircleOutlined />, text: 'Aktif' },
      inactive: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Pasif' },
      pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Beklemede' },
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const getCommunicationIcon = (type) => {
    const icons = {
      phone: <PhoneOutlined style={{ color: '#52c41a' }} />,
      email: <MailOutlined style={{ color: '#1890ff' }} />,
      visit: <TeamOutlined style={{ color: '#fa8c16' }} />,
      chat: <MessageOutlined style={{ color: '#722ed1' }} />,
    };
    return icons[type] || <FileTextOutlined style={{ color: '#d9d9d9' }} />;
  };

  const handleEdit = () => {
    form.setFieldsValue(customer);
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      await axios.put(`/api/v1/customers/${id}`, values);
      message.success('Müşteri başarıyla güncellendi');
      setEditModalVisible(false);
      fetchCustomerData();
    } catch (error) {
      handleApiError(error, 'Güncelleme başarısız', message.error);
    }
  };

  const handleDelete = async () => {
    Modal.confirm({
      title: 'Müşteriyi silmek istediğinizden emin misiniz?',
      content: 'Bu işlem geri alınamaz. Müşteriye ait tüm cihazlar ve faturalar da etkilenebilir.',
      okText: 'Evet, Sil',
      cancelText: 'İptal',
      okType: 'danger',
      onOk: async () => {
        try {
          await axios.delete(`/api/v1/customers/${id}`);
          message.success('Müşteri başarıyla silindi');
          navigate('/customers');
        } catch (error) {
          handleApiError(error, 'Silme işlemi başarısız', message.error);
        }
      },
    });
  };

  const handleAddCommunication = () => {
    communicationForm.resetFields();
    setCommunicationModalVisible(true);
  };

  const handleCommunicationSubmit = async (values) => {
    try {
      // Simulated communication add
      const newCommunication = {
        id: communications.length + 1,
        ...values,
        timestamp: new Date(),
        status: 'completed',
        agent: 'Mevcut Kullanıcı'
      };
      setCommunications([newCommunication, ...communications]);
      message.success('İletişim kaydı eklendi');
      setCommunicationModalVisible(false);
    } catch (error) {
      message.error('İletişim kaydı eklenemedi');
    }
  };

  if (loading) {
    return <div>Yükleniyor...</div>;
  }

  if (!customer) {
    return <Alert message="Müşteri bulunamadı" type="error" />;
  }

  const deviceColumns = [
    {
      title: 'Cihaz Adı',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <CheckCircleOutlined style={{ color: record.status === 'online' ? '#52c41a' : '#d9d9d9' }} />
          <span style={{ fontWeight: 'bold' }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'MAC Adresi',
      dataIndex: 'mac_address',
      key: 'mac_address',
      render: (text) => <code>{text}</code>,
    },
    {
      title: 'Seri Numarası',
      dataIndex: 'serial_number',
      key: 'serial_number',
      render: (text) => text || '-',
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: 'Son Görülme',
      dataIndex: 'last_seen',
      key: 'last_seen',
      render: (date) => {
        if (!date) return <span style={{ color: '#999' }}>Hiç görülmedi</span>;
        const lastSeen = new Date(date);
        const now = new Date();
        const diffHours = Math.floor((now - lastSeen) / (1000 * 60 * 60));
        
        if (diffHours < 1) {
          return <span style={{ color: '#52c41a' }}>Az önce</span>;
        } else if (diffHours < 24) {
          return <span style={{ color: '#fa8c16' }}>{diffHours} saat önce</span>;
        } else {
          return <span style={{ color: '#ff4d4f' }}>{lastSeen.toLocaleDateString('tr-TR')}</span>;
        }
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
                     <Button 
             type="primary" 
             size="small"
             icon={<EyeOutlined />}
             onClick={() => navigate(`/devices/${record.id}?from=customer`)}
           >
             Detay
           </Button>
        </Space>
      ),
    },
  ];

  const invoiceColumns = [
    {
      title: 'Fatura No',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (text) => <code>{text}</code>,
    },
    {
      title: 'Fatura Tarihi',
      dataIndex: 'issue_date',
      key: 'issue_date',
      render: (date) => {
        if (!date) return '-';
        try {
          return new Date(date).toLocaleDateString('tr-TR');
        } catch (error) {
          return 'Geçersiz Tarih';
        }
      },
    },
    {
      title: 'Vade Tarihi',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date) => {
        if (!date) return '-';
        try {
          return new Date(date).toLocaleDateString('tr-TR');
        } catch (error) {
          return 'Geçersiz Tarih';
        }
      },
    },
    {
      title: 'Tutar',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => `₺${parseFloat(amount || 0).toFixed(2)}`,
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          pending: { color: 'orange', text: 'Beklemede' },
          paid: { color: 'green', text: 'Ödendi' },
          overdue: { color: 'red', text: 'Gecikmiş' },
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
                     <Button 
             type="primary" 
             size="small"
             icon={<EyeOutlined />}
             onClick={() => navigate(`/billing/${record.id}?from=customer`)}
           >
             Detay
           </Button>
        </Space>
      ),
    },
  ];

  const communicationColumns = [
    {
      title: 'Tarih',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp) => timestamp.toLocaleString('tr-TR'),
    },
    {
      title: 'Tür',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Space>
          {getCommunicationIcon(type)}
          <Tag color="blue">{type.toUpperCase()}</Tag>
        </Space>
      ),
    },
    {
      title: 'Konu',
      dataIndex: 'subject',
      key: 'subject',
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          completed: { color: 'green', text: 'Tamamlandı' },
          pending: { color: 'orange', text: 'Beklemede' },
          sent: { color: 'blue', text: 'Gönderildi' },
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Temsilci',
      dataIndex: 'agent',
      key: 'agent',
    },
  ];

  const deviceStatusData = [
    { name: 'Çevrimiçi', value: devices.filter(d => d.status === 'online').length, color: '#52c41a' },
    { name: 'Çevrimdışı', value: devices.filter(d => d.status === 'offline').length, color: '#fa8c16' },
    { name: 'Hata', value: devices.filter(d => d.status === 'error').length, color: '#ff4d4f' },
  ];

  const invoiceStatusData = [
    { name: 'Beklemede', value: invoices.filter(i => i.status === 'pending').length, color: '#fa8c16' },
    { name: 'Ödendi', value: invoices.filter(i => i.status === 'paid').length, color: '#52c41a' },
    { name: 'Gecikmiş', value: invoices.filter(i => i.status === 'overdue').length, color: '#ff4d4f' },
  ];

  // Gerçek fatura verilerinden kullanım analizi oluştur
  // Usage data from session logs (real-time)
  const usageData = usageAnalytics?.monthly_usage?.map(item => ({
    month: item.month_name,
    sessions: item.session_count,
    shots: item.total_shots,
    joule: item.total_joule,
    duration: item.total_duration_seconds
  })) || [];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/customers')}>
            Geri
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            {customer.name}
          </Title>
          {getStatusTag(customer.is_active ? 'active' : 'inactive')}
          <Badge status={customer.is_active ? 'success' : 'error'} text={customer.is_active ? 'Aktif' : 'Pasif'} />
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchCustomerData}>
            Yenile
          </Button>
          {/* İletişim ekleme henüz implement edilmemiş */}
          <Button icon={<EditOutlined />} onClick={handleEdit}>
            Düzenle
          </Button>
          <Button icon={<DeleteOutlined />} danger onClick={handleDelete}>
            Sil
          </Button>
        </Space>
      </div>

      {/* Customer Profile Stats Summary */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <Space size="large" wrap>
            <span>
              <SettingOutlined style={{ color: '#1890ff', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Toplam Cihaz</Text>
              <Text strong style={{ fontSize: 18 }}>{devices.length}</Text>
            </span>
            <span>
              <Badge status="success" />
              <Text style={{ marginRight: 4 }}>Aktif Cihaz</Text>
              <Text strong style={{ color: '#3f8600', fontSize: 18 }}>{devices.filter(d => d.status === 'online').length}</Text>
            </span>
          </Space>
          <Space size="middle" wrap>
            <span>
              <DollarOutlined style={{ color: '#fa8c16', marginRight: 4 }} />
              <Text style={{ marginRight: 4 }}>Fatura</Text>
              <Text strong style={{ fontSize: 16 }}>{invoices.length}</Text>
            </span>
            <span>
              <DollarOutlined style={{ color: '#722ed1', marginRight: 4 }} />
              <Text style={{ marginRight: 4 }}>Toplam Tutar</Text>
              <Text strong style={{ color: '#722ed1', fontSize: 18 }}>
                {invoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0).toFixed(2)}
              </Text>
              <Text> ₺</Text>
            </span>
          </Space>
        </div>
      </Card>

             {/* Main Content */}
       <Tabs defaultActiveKey={activeTab}>
        <TabPane tab="Genel Bakış" key="overview">
          <Row gutter={16}>
            <Col span={12}>
              <Card title="Müşteri Bilgileri">
                <Descriptions column={1}>
                  <Descriptions.Item label="Müşteri Adı">{customer.name}</Descriptions.Item>
                  <Descriptions.Item label="İletişim Kişisi">{customer.contact_person || '-'}</Descriptions.Item>
                  <Descriptions.Item label="E-posta">{customer.contact_email || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Telefon">{customer.contact_phone || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Adres">{customer.address || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Vergi Numarası">{customer.tax_number || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Durum">
                    <Badge status={customer.is_active ? 'success' : 'error'} text={customer.is_active ? 'Aktif' : 'Pasif'} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Kayıt Tarihi">
                    {customer.created_at ? new Date(customer.created_at).toLocaleDateString('tr-TR') : '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
                         <Col span={12}>
               <Card title="Hızlı İstatistikler" style={{ height: '100%' }}>
                 <Row gutter={16} style={{ height: 'calc(100% - 57px)' }}>
                   <Col span={12} style={{ height: '100%' }}>
                     <Card size="small" title="Cihaz Durumu" style={{ height: '100%' }}>
                       <div style={{ height: 'calc(100% - 57px)', display: 'flex', flexDirection: 'column' }}>
                         <ResponsiveContainer width="100%" height="60%">
                           <PieChart>
                             <Pie
                               data={deviceStatusData}
                               cx="50%"
                               cy="50%"
                               labelLine={false}
                               label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                               outerRadius={35}
                               fill="#8884d8"
                               dataKey="value"
                             >
                               {deviceStatusData.map((entry, index) => (
                                 <Cell key={`cell-${index}`} fill={entry.color} />
                               ))}
                             </Pie>
                             <RechartsTooltip />
                           </PieChart>
                         </ResponsiveContainer>
                         <div style={{ marginTop: 8, fontSize: '12px', flex: 1 }}>
                           {deviceStatusData.map((entry, index) => (
                             <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                               <div style={{ 
                                 width: 8, 
                                 height: 8, 
                                 backgroundColor: entry.color, 
                                 marginRight: 4,
                                 borderRadius: '50%'
                               }} />
                               <span>{entry.name}: {entry.value}</span>
                             </div>
                           ))}
                         </div>
                       </div>
                     </Card>
                   </Col>
                   <Col span={12} style={{ height: '100%' }}>
                     <Card size="small" title="Fatura Durumu" style={{ height: '100%' }}>
                       <div style={{ height: 'calc(100% - 57px)', display: 'flex', flexDirection: 'column' }}>
                         <ResponsiveContainer width="100%" height="60%">
                           <PieChart>
                             <Pie
                               data={invoiceStatusData}
                               cx="50%"
                               cy="50%"
                               labelLine={false}
                               label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                               outerRadius={35}
                               fill="#8884d8"
                               dataKey="value"
                             >
                               {invoiceStatusData.map((entry, index) => (
                                 <Cell key={`cell-${index}`} fill={entry.color} />
                               ))}
                             </Pie>
                             <RechartsTooltip />
                           </PieChart>
                         </ResponsiveContainer>
                         <div style={{ marginTop: 8, fontSize: '12px', flex: 1 }}>
                           {invoiceStatusData.map((entry, index) => (
                             <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                               <div style={{ 
                                 width: 8, 
                                 height: 8, 
                                 backgroundColor: entry.color, 
                                 marginRight: 4,
                                 borderRadius: '50%'
                               }} />
                               <span>{entry.name}: {entry.value}</span>
                             </div>
                           ))}
                         </div>
                       </div>
                     </Card>
                   </Col>
                 </Row>
               </Card>
             </Col>
          </Row>
        </TabPane>

        <TabPane tab="Cihazlar" key="devices">
          <Card title="Müşteri Cihazları">
            <Table
              columns={deviceColumns}
              dataSource={devices}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </Card>
        </TabPane>

        <TabPane tab="Faturalar" key="invoices">
          <Card title="Müşteri Faturaları">
            <Table
              columns={invoiceColumns}
              dataSource={invoices}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          </Card>
        </TabPane>

        <TabPane tab={`Kiralamalar (${rentals.length})`} key="rentals">
          <Card
            title="Cihaz Kiralamaları"
            extra={
              <Space>
                <Button size="small" onClick={() => navigate('/rentals')}>Kiralama Yönetimi</Button>
                <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => navigate('/rentals')}>Yeni Kiralama</Button>
              </Space>
            }
          >
            <Table
              columns={[
                {
                  title: 'Cihaz', dataIndex: 'device_name', key: 'device_name',
                  render: (text, record) => <a onClick={() => navigate(`/devices/${record.device_id}`)}>{text || `#${record.device_id}`}</a>,
                },
                {
                  title: 'Başlangıç', dataIndex: 'start_date', key: 'start_date', width: 150,
                  render: (date) => date ? safeFormatTimestamp(date) : '-',
                },
                {
                  title: 'Bitiş', dataIndex: 'end_date', key: 'end_date', width: 150,
                  render: (date) => date ? safeFormatTimestamp(date) : <Tag color="green">Devam ediyor</Tag>,
                },
                {
                  title: 'Tip', dataIndex: 'billing_type', key: 'billing_type', width: 110,
                  render: (type) => <Tag color={type === 'fixed_monthly' ? 'blue' : 'purple'}>{type === 'fixed_monthly' ? 'Sabit Aylık' : 'Atış Bazlı'}</Tag>,
                },
                {
                  title: 'Ücret', key: 'rate', width: 130,
                  render: (_, r) => r.billing_type === 'fixed_monthly'
                    ? (r.monthly_rate ? `₺${parseFloat(r.monthly_rate).toFixed(2)}/ay` : '-')
                    : (r.unit_price ? `₺${parseFloat(r.unit_price).toFixed(4)}/atış` : '-'),
                },
                {
                  title: 'Durum', key: 'status', width: 80,
                  render: (_, r) => r.is_active ? <Tag color="green">Aktif</Tag> : <Tag>Bitti</Tag>,
                },
                {
                  title: '', key: 'actions', width: 50, align: 'center',
                  render: (_, record) => record.is_active ? (
                    <Popconfirm title="Kiralama sonlandırılsın mı?" okText="Evet" cancelText="Hayır"
                      onConfirm={async () => {
                        try {
                          const now = dayjs().toISOString();
                          await axios.post(`/api/v1/rentals/${record.id}/end?end_date=${encodeURIComponent(now)}`);
                          message.success('Kiralama sonlandırıldı');
                          // Refresh rentals
                          const resp = await axios.get(`/api/v1/rentals?customer_id=${id}`);
                          setRentals(Array.isArray(resp.data) ? resp.data : []);
                        } catch (e) { message.error(e.response?.data?.detail || 'Hata'); }
                      }}
                    >
                      <Tooltip title="Sonlandır"><Button icon={<StopOutlined />} size="small" type="text" danger /></Tooltip>
                    </Popconfirm>
                  ) : null,
                },
              ]}
              dataSource={rentals}
              rowKey="id"
              pagination={{ pageSize: 20 }}
              size="small"
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <TeamOutlined />
              Kullanıcılar ({users.length})
            </span>
          }
          key="users"
        >
          <Card
            title="Müşteri Kullanıcıları"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setUserModalVisible(true)}
              >
                Kullanıcı Ekle
              </Button>
            }
          >
            <Table
              columns={[
                {
                  title: 'Kullanıcı Adı',
                  dataIndex: 'username',
                  key: 'username',
                  render: (text, record) => (
                    <Space>
                      <UserOutlined style={{ color: record.is_active ? '#52c41a' : '#d9d9d9' }} />
                      <span style={{ fontWeight: 'bold' }}>{text}</span>
                    </Space>
                  ),
                },
                {
                  title: 'Ad Soyad',
                  dataIndex: 'full_name',
                  key: 'full_name',
                },
                {
                  title: 'E-posta',
                  dataIndex: 'email',
                  key: 'email',
                  render: (email) => email ? <a href={`mailto:${email}`}>{email}</a> : '-',
                },
                {
                  title: 'Durum',
                  dataIndex: 'is_active',
                  key: 'is_active',
                  render: (active) => (
                    <Badge
                      status={active ? 'success' : 'error'}
                      text={active ? 'Aktif' : 'Pasif'}
                    />
                  ),
                },
                {
                  title: 'İşlemler',
                  key: 'actions',
                  render: (_, record) => (
                    <Space>
                      <Tooltip title="Profili Görüntüle">
                        <Button
                          type="text"
                          icon={<EyeOutlined />}
                          onClick={() => navigate(`/users/${record.id}`)}
                        />
                      </Tooltip>
                      <Tooltip title="Müşteriden Kaldır">
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            Modal.confirm({
                              title: 'Bu kullanıcıyı müşteriden kaldırmak istediğinize emin misiniz?',
                              content: 'Kullanıcı silinmeyecek, sadece bu müşteriyle ilişkisi kaldırılacaktır.',
                              okText: 'Evet, Kaldır',
                              cancelText: 'İptal',
                              okType: 'danger',
                              onOk: () => handleRemoveUser(record.id)
                            });
                          }}
                        />
                      </Tooltip>
                    </Space>
                  ),
                },
              ]}
              dataSource={users}
              rowKey="id"
              loading={usersLoading}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'Bu müşteriye atanmış kullanıcı bulunmuyor' }}
            />
          </Card>
        </TabPane>

        {/* İletişim Geçmişi tabı henüz implement edilmemiş */}

        <TabPane tab="Kullanım Analizi" key="analytics">
          {/* Date Range Picker */}
          <Card style={{ marginBottom: 16 }}>
            <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
              <div>
                <Text strong style={{ marginRight: 12 }}>Tarih Aralığı:</Text>
                <RangePicker
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  format="DD.MM.YYYY"
                  placeholder={['Başlangıç', 'Bitiş']}
                  style={{ width: 280 }}
                  allowClear={false}
                  ranges={{
                    'Son 7 Gün': [dayjs().subtract(7, 'days'), dayjs()],
                    'Son 30 Gün': [dayjs().subtract(30, 'days'), dayjs()],
                    'Son 3 Ay': [dayjs().subtract(3, 'months'), dayjs()],
                    'Son 6 Ay': [dayjs().subtract(6, 'months'), dayjs()],
                    'Son 1 Yıl': [dayjs().subtract(1, 'year'), dayjs()],
                  }}
                />
              </div>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={() => fetchUsageAnalytics(dateRange[0].toDate(), dateRange[1].toDate())}
                loading={analyticsLoading}
              >
                Yenile
              </Button>
            </Space>
          </Card>

          <Card size="small" style={{ marginBottom: 16 }}>
            <Space size="large" wrap>
              <span>
                <HistoryOutlined style={{ color: '#1890ff', marginRight: 6 }} />
                <Text style={{ marginRight: 4 }}>Toplam Seans</Text>
                <Text strong style={{ fontSize: 18 }}>{usageAnalytics?.total_sessions || 0}</Text>
              </span>
              <span>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                <Text style={{ marginRight: 4 }}>Toplam Atış</Text>
                <Text strong style={{ fontSize: 18 }}>{usageAnalytics?.total_shots || 0}</Text>
              </span>
              <span>
                <DollarOutlined style={{ color: '#722ed1', marginRight: 6 }} />
                <Text style={{ marginRight: 4 }}>Toplam Enerji</Text>
                <Text strong style={{ fontSize: 18 }}>{(usageAnalytics?.total_joule || 0).toFixed(2)}</Text>
                <Text> J</Text>
              </span>
            </Space>
          </Card>
          <Row gutter={16}>
            <Col span={24}>
              <Card 
                title="Aylık Kullanım Trendi"
                loading={analyticsLoading}
              >
                {usageData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={usageData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <RechartsTooltip />
                      <Legend />
                      <Line
                        yAxisId="left"
                        type="monotone"
                        dataKey="shots"
                        stroke="#1890ff"
                        strokeWidth={2}
                        name="Atış Sayısı"
                        dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="sessions"
                        stroke="#52c41a"
                        strokeWidth={2}
                        name="Seans Sayısı"
                        dot={{ fill: '#52c41a', strokeWidth: 2, r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Alert 
                    message="Henüz kullanım verisi yok" 
                    description="Bu müşteriye ait cihazlardan henüz seans logu alınmamış." 
                    type="info" 
                    showIcon 
                  />
                )}
              </Card>
            </Col>
          </Row>
        </TabPane>
      </Tabs>

      {/* Edit Modal */}
      <Modal
        title="Müşteri Düzenle"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose={true}
      >
        <Form form={form} onFinish={handleEditSubmit} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Müşteri Adı"
                rules={customerNameRules()}
              >
                <Input prefix={<TeamOutlined />} placeholder="Örn: ABC Şirketi Ltd." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contact_person"
                label="İletişim Kişisi"
                rules={contactPersonRules({ required: true })}
              >
                <Input prefix={<UserOutlined />} placeholder="Ad Soyad" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="contact_email"
                label="E-posta"
                rules={emailRules({ required: true })}
              >
                <Input prefix={<MailOutlined />} placeholder="iletisim@sirket.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contact_phone"
                label="Telefon"
                rules={phoneRules({ required: true })}
              >
                <Input prefix={<PhoneOutlined />} placeholder="+90 555 123 4567" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Adres"
            rules={addressRules({ required: true })}
          >
            <TextArea rows={3} placeholder="Tam adres" />
          </Form.Item>

          <Form.Item
            name="tax_number"
            label="Vergi Numarası"
            rules={taxNumberRules()}
          >
            <Input placeholder="Opsiyonel" />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Hesap Durumu"
            valuePropName="checked"
          >
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Güncelle
              </Button>
              <Button onClick={() => setEditModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Communication Modal */}
      <Modal
        title="İletişim Kaydı Ekle"
        open={communicationModalVisible}
        onCancel={() => setCommunicationModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={communicationForm} onFinish={handleCommunicationSubmit} layout="vertical">
          <Form.Item
            name="type"
            label="İletişim Türü"
            rules={[{ required: true, message: 'İletişim türü gerekli' }]}
          >
            <Select>
              <Option value="phone">Telefon</Option>
              <Option value="email">E-posta</Option>
              <Option value="visit">Ziyaret</Option>
              <Option value="chat">Chat</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="direction"
            label="Yön"
            rules={[{ required: true, message: 'Yön gerekli' }]}
          >
            <Select>
              <Option value="inbound">Gelen</Option>
              <Option value="outbound">Giden</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="subject"
            label="Konu"
            rules={[{ required: true, message: 'Konu gerekli' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="content"
            label="İçerik"
            rules={[{ required: true, message: 'İçerik gerekli' }]}
          >
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Ekle
              </Button>
              <Button onClick={() => setCommunicationModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* User Modal */}
      <Modal
        title="Kullanıcı Ekle"
        open={userModalVisible}
        onCancel={() => {
          setUserModalVisible(false);
          setUserModalTab('existing');
          userForm.resetFields();
        }}
        footer={null}
        width={700}
        destroyOnClose={true}
      >
        <Tabs activeKey={userModalTab} onChange={setUserModalTab}>
          <TabPane tab="Mevcut Kullanıcı Ekle" key="existing">
            <Alert
              message="Mevcut Kullanıcı Ekleme"
              description="Sistemde kayıtlı olan customer rolündeki bir kullanıcıyı bu müşteriye atayabilirsiniz."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Form
              form={userForm}
              onFinish={handleAddExistingUser}
              layout="vertical"
            >
              <Form.Item
                name="user_id"
                label="Kullanıcı Seçin"
                rules={[{ required: true, message: 'Lütfen bir kullanıcı seçin' }]}
              >
                <Select
                  showSearch
                  placeholder="Kullanıcı seçin..."
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {availableUsers
                    .filter(u => !users.find(existingUser => existingUser.id === u.id))
                    .map(user => (
                      <Option key={user.id} value={user.id}>
                        {user.username} {user.full_name ? `(${user.full_name})` : ''} - {user.email || 'E-posta yok'}
                      </Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    Kullanıcı Ekle
                  </Button>
                  <Button onClick={() => {
                    setUserModalVisible(false);
                    setUserModalTab('existing');
                    userForm.resetFields();
                  }}>
                    İptal
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="Yeni Kullanıcı Oluştur" key="new">
            <Alert
              message="Yeni Kullanıcı Oluşturma"
              description="Yeni bir customer rolü kullanıcısı oluşturup doğrudan bu müşteriye atayabilirsiniz."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Form
              form={userForm}
              onFinish={handleCreateNewUser}
              layout="vertical"
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="username"
                    label="Kullanıcı Adı"
                    rules={usernameRules()}
                  >
                    <Input prefix={<UserOutlined />} placeholder="johndoe" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="password"
                    label="Şifre"
                    rules={passwordRules(passwordPolicy, { required: true })}
                    extra={passwordHelpText(passwordPolicy)}
                  >
                    <Input.Password placeholder="Güçlü bir şifre girin" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="full_name"
                    label="Ad Soyad"
                    rules={fullNameRules()}
                  >
                    <Input prefix={<UserOutlined />} placeholder="John Doe" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="email"
                    label="E-posta"
                    rules={emailRules({ required: true })}
                  >
                    <Input prefix={<MailOutlined />} placeholder="john@example.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="is_active"
                label="Hesap Durumu"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" defaultChecked />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit">
                    Oluştur ve Ekle
                  </Button>
                  <Button onClick={() => {
                    setUserModalVisible(false);
                    setUserModalTab('existing');
                    userForm.resetFields();
                  }}>
                    İptal
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>
      </Modal>
    </div>
  );
};

export default CustomerProfile;
